if ( typeof CompareBar !== 'function' ) {

	class CompareBar extends HTMLElement {

		constructor(){
			super();
			const compare = window.KROWN?.compare || {};
			this.MAX_PRODUCTS = compare.maxProducts || 4;
			this.STORAGE_KEY = compare.storageKey || 'kt-compare-products';
			this.RETURN_KEY = compare.returnKey || 'kt-compare-return-url';
			this.selectedProducts = [];

			// Bound once so they can be removed in disconnectedCallback (global listeners would otherwise leak on section re-render).
			this.onDocumentCheckboxChange = this.onDocumentCheckboxChange.bind(this);
			this.onGridFiltered = this.onGridFiltered.bind(this);
			this.onPageShow = this.onPageShow.bind(this);
			this.onWindowScroll = this.onWindowScroll.bind(this);
		}

		onWindowScroll(){
			const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 20;
			this.classList.toggle('kt-product-compare--at-page-bottom', atBottom);
		}

		onDocumentCheckboxChange(e){
			if ( e.target.closest('compare-checkbox') ) {
				const checkbox = e.target;
				const parent = checkbox.closest('compare-checkbox');
				if ( checkbox.checked ) {
					this.#addProduct(parent);
				} else {
					const index = this.selectedProducts.findIndex(p => this.#productId(p.id) === this.#productId(parent.dataset.id));
					if ( index > -1 ) {
						this.#removeProduct(index);
					}
				}
			}
		}

		onGridFiltered(){
			this.#syncCheckboxes();
			this.#updateLimitState();
		}

		onPageShow(e){
			if ( !e.persisted ) return;
			this.selectedProducts = this.#loadStored();
			this.#syncCheckboxes();
			this.#render();
		}

		#productId(value){
			// `== null` is deliberate: it catches undefined too (a missing dataset attribute or a
			// stored entry with no id). A strict check lets undefined through as the string "undefined".
			return value == null || value === '' ? '' : String(value);
		}

		#appendPlaceholderImage(link){
			const template = document.getElementById('product-compare-image-placeholder');
			if ( template ) {
				link.appendChild(template.content.cloneNode(true));
			}
		}

		connectedCallback(){

			this.items = this.querySelectorAll('.kt-product-compare__item');
			this.countText = this.querySelector('.kt-product-compare__count-text');
			this.clearButton = this.querySelector('.kt-product-compare__clear');
			this.statusEl = this.querySelector('.kt-product-compare__status');
			this.countProducts = this.dataset.countTemplate || '[count] of [total] selected';
			this.maxMessage = this.dataset.maxMessage || '';

			this.selectedProducts = this.#loadStored();

			this.#restoreCheckboxes();

			this.addEventListener('click', (e) => {
				const compareBtn = e.target.closest('.kt-product-compare__button');
				if ( compareBtn && compareBtn.getAttribute('aria-disabled') === 'true' ) {
					e.preventDefault();
					return;
				}

				const closeBtn = e.target.closest('[data-js-compare-close]');
				if ( closeBtn ) {
					e.preventDefault();
					const item = closeBtn.closest('.kt-product-compare__item');
					const allItems = [...this.querySelectorAll('.kt-product-compare__item')];
					const index = allItems.indexOf(item);
					if ( index > -1 && this.selectedProducts[index] ) {
						this.#removeProduct(index);
					}
				}
			});

			if ( this.clearButton ) {
				this.clearButton.addEventListener('click', (e) => {
					e.preventDefault();
					this.#clearAll();
				});
			}

			document.addEventListener('change', this.onDocumentCheckboxChange);
			document.addEventListener('krown:main-collection-grid:filtered', this.onGridFiltered);
			window.addEventListener('pageshow', this.onPageShow);
			window.addEventListener('scroll', this.onWindowScroll, { passive: true });
			window.addEventListener('resize', this.onWindowScroll, { passive: true });

			this.#render();
			this.onWindowScroll();

		}

		disconnectedCallback(){
			document.removeEventListener('change', this.onDocumentCheckboxChange);
			document.removeEventListener('krown:main-collection-grid:filtered', this.onGridFiltered);
			window.removeEventListener('pageshow', this.onPageShow);
			window.removeEventListener('scroll', this.onWindowScroll);
			window.removeEventListener('resize', this.onWindowScroll);
		}

		#loadStored(){
			try {
				const data = JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || [];
				return data.map(p => ({ ...p, id: this.#productId(p.id) }));
			} catch(e) {
				return [];
			}
		}

		#syncCheckboxes(){
			const selected = {};
			this.selectedProducts.forEach(p => { selected[this.#productId(p.id)] = p; });
			document.querySelectorAll('compare-checkbox').forEach(el => {
				const checkbox = el.querySelector('input[type="checkbox"]');
				const product = selected[this.#productId(el.dataset.id)];
				if ( checkbox ) checkbox.checked = !!product;
				if ( product ) product.checkboxEl = el;
			});
			this.#updateLimitState();
		}

		// Once the max is reached, mark every still-unselected checkbox as disabled
		// so it shows the "limit reached" / forbidden indicator and can't be toggled.
		#updateLimitState(){
			const limitReached = this.selectedProducts.length >= this.MAX_PRODUCTS;
			const selected = {};
			this.selectedProducts.forEach(p => { selected[this.#productId(p.id)] = true; });
			document.querySelectorAll('compare-checkbox').forEach(el => {
				const isSelected = !!selected[this.#productId(el.dataset.id)];
				const disabled = limitReached && !isSelected;
				el.classList.toggle('kt-product-item__compare--disabled', disabled);
				const checkbox = el.querySelector('input[type="checkbox"]');
				if ( checkbox ) checkbox.setAttribute('aria-disabled', disabled ? 'true' : 'false');
			});
		}

		#restoreCheckboxes(){
			this.#syncCheckboxes();

			const observer = new MutationObserver(() => {
				this.#syncCheckboxes();
				if ( this.selectedProducts.every(p => p.checkboxEl) ) observer.disconnect();
			});
			const observerTarget = document.getElementById('main-collection-product-grid') || document.body;
			observer.observe(observerTarget, { childList: true, subtree: true });

			setTimeout(() => observer.disconnect(), 10000);
		}

		#persist(key, value){
			try {
				localStorage.setItem(key, value);
			} catch(e) {}
		}

		#save(){
			const cleanData = this.selectedProducts.map(p => ({
				id: this.#productId(p.id),
				title: p.title,
				url: p.url,
				handle: p.handle,
				image: p.image
			}));
			this.#persist(this.STORAGE_KEY, JSON.stringify(cleanData));
			// Remember where the products were selected from, so "back to all products"
			// on the compare page can return to this exact collection (with its filters).
			this.#persist(this.RETURN_KEY, window.location.pathname + window.location.search);
		}

		#addProduct(el){
			if ( this.selectedProducts.length >= this.MAX_PRODUCTS ) {
				const checkbox = el.querySelector('input[type="checkbox"]');
				if ( checkbox ) checkbox.checked = false;
				if ( this.statusEl && this.maxMessage ) {
					this.statusEl.textContent = this.maxMessage;
				}
				return;
			}
			if ( this.selectedProducts.some(p => this.#productId(p.id) === this.#productId(el.dataset.id)) ) return;

			this.selectedProducts.push({
				id: this.#productId(el.dataset.id),
				title: el.dataset.title,
				url: el.dataset.url,
				handle: el.dataset.handle,
				image: el.dataset.image || '',
				checkboxEl: el
			});

			this.#save();
			this.#render();
		}

		#removeProduct(index){
			const product = this.selectedProducts[index];
			if ( product && product.checkboxEl ) {
				const checkbox = product.checkboxEl.querySelector('input[type="checkbox"]');
				if ( checkbox ) checkbox.checked = false;
			}
			this.selectedProducts.splice(index, 1);
			this.#save();
			this.#render();
		}

		#clearAll(){
			this.selectedProducts.forEach(p => {
				if ( p.checkboxEl ) {
					const checkbox = p.checkboxEl.querySelector('input[type="checkbox"]');
					if ( checkbox ) checkbox.checked = false;
				}
			});
			this.selectedProducts = [];
			this.#save();
			this.#render();
		}

		#render(){

			this.items.forEach((item, i) => {
				const placeholder = item.querySelector('.kt-product-compare__item-placeholder');
				const closeBtn = item.querySelector('[data-js-compare-close]');

				if ( this.selectedProducts[i] ) {
					const product = this.selectedProducts[i];
					if ( product.image ) {
						const link = document.createElement('a');
						link.href = product.url;
						const img = document.createElement('img');
						img.src = product.image;
						img.alt = product.title;
						link.appendChild(img);
						placeholder.replaceChildren(link);
					} else {
						const link = document.createElement('a');
						link.href = product.url;
						const fallback = this.dataset.fallbackImage;
						if ( fallback ) {
							const img = document.createElement('img');
							img.src = fallback;
							img.alt = product.title;
							link.appendChild(img);
						} else {
							this.#appendPlaceholderImage(link);
						}
						placeholder.replaceChildren(link);
					}
					placeholder.classList.add('selected');
					if ( closeBtn ) closeBtn.style.display = '';
				} else {
					placeholder.replaceChildren();
					placeholder.classList.remove('selected');
					if ( closeBtn ) closeBtn.style.display = 'none';
				}
			});

			if ( this.countText ) {
				this.countText.textContent = this.countProducts
					.replace('[count]', this.selectedProducts.length)
					.replace('[total]', this.MAX_PRODUCTS);
			}

			if ( this.selectedProducts.length > 0 ) {
				this.classList.add('kt-product-compare--active');
			} else {
				this.classList.remove('kt-product-compare--active');
			}

			const compareButton = this.querySelector('.kt-product-compare__button');

			if (compareButton) {
				if (this.selectedProducts.length > 1) {
					const handles = this.selectedProducts.map(p => p.handle).join(',');
					const firstProduct = this.selectedProducts[0];
					const firstUrl = firstProduct.url || (firstProduct.handle ? `/products/${firstProduct.handle}` : '/');

					const compareUrl = new URL(firstUrl, window.location.origin);
					compareUrl.searchParams.set('view', 'compare');
					compareUrl.searchParams.set('compare', handles);
					compareButton.href = compareUrl.pathname + compareUrl.search;
					compareButton.setAttribute('aria-disabled', 'false');
					compareButton.removeAttribute('role');
					compareButton.removeAttribute('tabindex');
				} else {
					compareButton.href = '#';
					compareButton.setAttribute('aria-disabled', 'true');
					compareButton.setAttribute('role', 'button');
					compareButton.setAttribute('tabindex', '-1');
				}
			}

			this.#updateLimitState();

		}

	}

  if ( typeof customElements.get('compare-bar') === 'undefined' ) {
		customElements.define('compare-bar', CompareBar);
	}

}
